import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  LubavContact,
  LubavSessionType,
  MainResponse,
  RESOURCE_KEY,
  ResourcesContent,
} from '@backrs_ui/grow-ui/src/models/models';
import { getSessionState } from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import { RootState } from '@backrs_ui/grow-ui/src/redux/store';

import { AxiosInstance } from 'axios';

export const handleFetchAllProfileImages = async (
  contacts: LubavContact[],
  axios: AxiosInstance,
) => {
  const newProfileImages: Record<string, string> = {};

  for (const contact of contacts) {
    try {
      const endpoint =
        contact.metadata.profile_type === 'external'
          ? '/api/get-external-profile-image'
          : '/api/get-backr-user-profile-image';

      const response = await axios.post(endpoint, contact.id, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 422) {
        continue;
      }

      if (response.data) {
        newProfileImages[contact.id] = response.data;
      }
    } catch (error) {
      console.error('Error fetching profile image:', error);
    }
  }

  return newProfileImages;
};

export const fetchMainResponseProfileImage = createAsyncThunk<
  MainResponse | undefined,
  { axios: AxiosInstance; sessionType: string },
  { state: RootState }
>('boxData/fetchMainResponseProfileImage', async ({ axios, sessionType }, { getState }) => {
  const { main_response } =
    getSessionState(getState(), sessionType)?.boxData?.boxData?.mainResponse ?? {};

  if (!main_response || !main_response.id || !main_response.metadata) {
    return main_response;
  }

  const contact: LubavContact = {
    id: main_response.id,
    relevance: main_response.relevance,
    metadata: main_response.metadata,
  };

  const newProfileImages = await handleFetchAllProfileImages([contact], axios);

  const updatedMainResponse: MainResponse = {
    ...main_response,
    metadata: { ...main_response.metadata, profile_image: newProfileImages[main_response.id] },
  };

  return updatedMainResponse;
});

const updateContacts = (contacts: LubavContact[], newProfileImages: Record<string, string>) =>
  contacts.map((contact) =>
    newProfileImages[contact.id]
      ? {
          ...contact,
          metadata: {
            ...contact.metadata,
            profile_image: newProfileImages[contact.id],
          },
        }
      : contact,
  );

export const fetchProfileImages = createAsyncThunk<
  ResourcesContent | undefined,
  { axios: AxiosInstance; activeSession: LubavSessionType | null },
  { state: RootState }
>('boxData/fetchProfileImages', async ({ axios, activeSession }, { getState }) => {
  if (!activeSession) {
    return undefined;
  }

  const { resources } =
    getSessionState(getState(), activeSession)?.boxData?.boxData?.mainResponse ?? {};

  if (!resources) {
    return resources;
  }

  const profileKeysInResources = [
    RESOURCE_KEY.EXTERNAL_PROFILES,
    RESOURCE_KEY.STUDENTS,
    RESOURCE_KEY.BACKRS,
  ] as const;

  // combine all the external profiles, students, and backrs into one array
  const contacts: LubavContact[] = profileKeysInResources.reduce((acc, key) => {
    return acc.concat(resources[key]?.content ?? []);
  }, [] as LubavContact[]);

  // fetch profile images for all contacts in the combo array
  const newProfileImages = await handleFetchAllProfileImages(contacts, axios);

  // update the profile images for all contacts in the resources state
  const updatedResources: ResourcesContent = {
    ...resources,
    ...profileKeysInResources.reduce((acc, key) => {
      return {
        ...acc,
        [key]: {
          content: updateContacts(resources[key]?.content ?? [], newProfileImages),
        },
      };
    }, {}),
  };

  return updatedResources;
});
